import { useEffect, useMemo, useRef, useState } from "react";
import { DTC_CODES } from "@/data/dtc_codes";
import { Textarea } from "@/components/ui/textarea";
import { MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Textarea with inline DTC autocomplete.
 * When user types "P" followed by digits at the end, we show a floating list
 * of matching codes with plain-English descriptions. Selecting one inserts
 * the code + description into the textarea.
 */
export default function DtcTextarea({ value, onChange, placeholder, testId }) {
  const [openList, setOpenList] = useState(false);
  const [caret, setCaret] = useState(0);
  const textareaRef = useRef(null);

  const activeToken = useMemo(() => {
    const before = value.slice(0, caret);
    // Match a P/B/C/U code being typed at the caret
    const m = before.match(/([PBCU])(\d{0,4})$/i);
    if (!m) return null;
    return { start: caret - m[0].length, prefix: m[0].toUpperCase() };
  }, [value, caret]);

  const matches = useMemo(() => {
    if (!activeToken) return [];
    const q = activeToken.prefix;
    return DTC_CODES.filter((c) => c.code.startsWith(q)).slice(0, 6);
  }, [activeToken]);

  useEffect(() => {
    setOpenList(matches.length > 0);
  }, [matches]);

  const onSelect = (item) => {
    if (!activeToken) return;
    const before = value.slice(0, activeToken.start);
    const after = value.slice(caret);
    const insertion = `${item.code} (${item.desc})`;
    const next = `${before}${insertion}${after.startsWith(" ") || after === "" ? "" : " "}${after}`;
    onChange(next);
    setOpenList(false);
    // restore caret after inserted text
    setTimeout(() => {
      const pos = before.length + insertion.length;
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(pos, pos);
        setCaret(pos);
      }
    }, 0);
  };

  const handleChange = (e) => {
    onChange(e.target.value);
    setCaret(e.target.selectionStart);
  };
  const handleSelect = (e) => setCaret(e.target.selectionStart);

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onSelect={handleSelect}
        onBlur={() => setTimeout(() => setOpenList(false), 150)}
        placeholder={placeholder}
        className="min-h-32 font-mono-tech bg-input"
        data-testid={testId}
      />
      {openList && (
        <div
          className="absolute left-0 right-0 mt-1 z-30 border border-border rounded-md bg-popover shadow-lg overflow-hidden"
          data-testid="dtc-suggestions"
        >
          <div className="px-3 py-1.5 text-[10px] font-mono-tech text-muted-foreground uppercase tracking-widest border-b border-border flex items-center gap-2">
            <MagnifyingGlass weight="bold" className="w-3 h-3" /> DTC matches for {activeToken?.prefix}
          </div>
          {matches.map((m) => (
            <button
              key={m.code}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onSelect(m); }}
              className="w-full text-left px-3 py-2 hover:bg-secondary transition-colors border-b border-border last:border-b-0"
              data-testid={`dtc-option-${m.code}`}
            >
              <div className="font-mono-tech text-sm text-primary">{m.code}</div>
              <div className="text-xs text-white/80">{m.desc}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
